import { ReactElement, ReactNode, useState } from "react";
import { useSupplierRelationshipList } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { InfiniteList } from "../infiniteLoading/InfiniteList";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useDebounce } from "use-debounce";
import { twMerge } from "tailwind-merge";
import { NavigationTabPanels } from "../navigationTabs/NavigationTabPanels";
import { NavigationTabGroup } from "../navigationTabs/NavigationTabsGroup";
import { Button, Card, Icon, InputText } from "@sellar-io/components";
import { useCustomerZonesList } from "../../hooks/api/v2/customerZones/useCustomerZonesList";
import { NavigationTabList } from "../navigationTabs/NavigationTabList";
import { pluralize } from "@sellar-io/common";
import { AllFulfillmentTypesType } from "@sellar-io/api-v2/types";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import {
  EntityAssignmentContext,
  useEntityAssignmentContext,
} from "./EntityAssignmentWizard.shared";

type EntityAssignmentType = {
  connectionIds: number[];
  customerZoneIds: number[];
  setConnectionIds: React.Dispatch<React.SetStateAction<number[]>>;
  setCustomerZoneIds: React.Dispatch<React.SetStateAction<number[]>>;
  existingConnectionIds?: number[];
  existingCustomerZoneIds?: number[];
  currentListName: string;
  header: ReactNode;
  type?:
    | "product catalogue"
    | "price list"
    | "delivery method"
    | "volume discount";
  fulfillmentType?: Extract<
    AllFulfillmentTypesType,
    "haulier" | "direct" | "courier" | "sellarDistribution"
  >;
  hideCustomerZones?: boolean;
};

type ItemType = {
  id: number;
  name: string;
  currentAssignment?: string | null;
};

export function EntityAssignmentWizard({
  connectionIds,
  customerZoneIds,
  setConnectionIds,
  setCustomerZoneIds,
  existingConnectionIds,
  existingCustomerZoneIds,
  currentListName,
  header,
  type,
  fulfillmentType,
  hideCustomerZones,
}: EntityAssignmentType): ReactElement {
  const unbundling4cEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  return (
    <EntityAssignmentContext
      value={{
        connectionIds,
        customerZoneIds,
        setConnectionIds,
        setCustomerZoneIds,
        existingConnectionIds,
        existingCustomerZoneIds,
        currentListName,
        header,
        type,
        fulfillmentType,
        hideCustomerZones,
      }}
    >
      <NavigationTabGroup
        tabs={
          hideCustomerZones
            ? ["Customers"]
            : ([
                ...(unbundling4cEnabled
                  ? ["Delivery Zones"]
                  : ["Customer Zones"]),
                "Customers",
              ] as const)
        }
        panelFactory={(tab) => {
          switch (tab) {
            case "Delivery Zones":
            case "Customer Zones":
              return <CustomerZones />;
            case "Customers":
              return <Customers />;
            default:
              return null;
          }
        }}
      >
        <div className="mx-auto flex max-w-[920px] flex-col gap-4 pb-[200px]">
          <div className="flex items-center gap-2">
            {currentListName}
            <Icon icon="CHEVRON_RIGHT" />
            <span className="font-semibold">Assign</span>
          </div>
          <Card className="flex flex-col gap-1 bg-white p-4">{header}</Card>
          <Card className="flex w-full flex-col gap-4 bg-white p-4">
            {!hideCustomerZones && <NavigationTabList variant="button" />}
            <NavigationTabPanels />
          </Card>
        </div>
      </NavigationTabGroup>
    </EntityAssignmentContext>
  );
}

export const CustomerZones = () => {
  const unbundling4cEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const {
    customerZoneIds,
    setCustomerZoneIds,
    existingCustomerZoneIds = [],
    type,
  } = useEntityAssignmentContext();

  const [customerZonesSelected, setCustomerZonesSelected] = useState<
    ItemType[]
  >([]);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const customerZoneListQuery = useCustomerZonesList({
    search: debouncedSearch,
    limit: 20,
  });

  const customerZoneList = customerZoneListQuery.data?.pages.flat() ?? [];

  const toggleCustomerZone = (customerZone: ItemType) => {
    setCustomerZoneIds((prev) =>
      prev.includes(customerZone.id)
        ? prev.filter((customerZoneId) => customerZoneId !== customerZone.id)
        : [...prev, customerZone.id],
    );

    setCustomerZonesSelected((prev) => {
      const existing = prev.find((c) => c.id === customerZone.id);
      if (existing) {
        return prev.filter((c) => c.id !== customerZone.id);
      }
      return [...prev, customerZone];
    });
  };

  return (
    <div className="flex flex-col gap-4 pb-34">
      <div className="flex flex-col gap-2">
        <InputLabel>
          Search {unbundling4cEnabled ? "delivery zones" : "customer zones"}
        </InputLabel>
        <InputContainer>
          <Icon icon="MAGNIFY" />
          <InputText onChange={(e) => setSearch(e.target.value)} />
        </InputContainer>
      </div>
      {customerZonesSelected.length > 0 && (
        <div className="flex w-full flex-col overflow-y-auto rounded-lg border border-zinc-300 bg-zinc-50">
          {customerZonesSelected.map((item, idx) => (
            <Item
              key={item.id}
              item={item}
              selected={true}
              alreadyAssigned={false}
              onToggle={toggleCustomerZone}
              noBorder={idx + 1 === customerZonesSelected.length}
            />
          ))}
        </div>
      )}
      <InfiniteList
        query={customerZoneListQuery}
        className="flex w-full flex-col rounded-lg border border-zinc-300"
      >
        {customerZoneList
          .filter((c) => !customerZonesSelected.find((d) => d.id == c.id))
          .filter((c) => type !== "delivery method" || !c.systemDefinedType)
          .map((customerZone, idx) => {
            const currentAssignment =
              type === "product catalogue"
                ? customerZone.AvailabilityTier?.name
                : customerZone.PriceTier?.name;
            return (
              <Item
                key={customerZone.id}
                item={{
                  id: customerZone.id,
                  name:
                    customerZone.name ??
                    (customerZone.type === "direct" ? "Local" : "Nationwide"),
                  currentAssignment,
                }}
                selected={customerZoneIds.includes(customerZone.id)}
                alreadyAssigned={existingCustomerZoneIds.includes(
                  customerZone.id,
                )}
                onToggle={toggleCustomerZone}
                noBorder={
                  idx + 1 ===
                  customerZoneList.length - customerZonesSelected.length
                }
              />
            );
          })}
      </InfiniteList>
    </div>
  );
};

const Customers = () => {
  const {
    connectionIds,
    setConnectionIds,
    existingConnectionIds = [],
    type,
    fulfillmentType,
  } = useEntityAssignmentContext();

  const [connectionsSelected, setConnectionsSelected] = useState<ItemType[]>(
    [],
  );

  const [selectedCustomersExpanded, setSelectedCustomersExpanded] =
    useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const supplierRelationshipListQuery = useSupplierRelationshipList({
    query:
      fulfillmentType &&
      ["direct", "courier", "haulier"].includes(fulfillmentType)
        ? [
            {
              type: "includeDeliveryMethods",
              value: fulfillmentType as "direct" | "courier" | "haulier",
              modifier: "EQUALS",
            } as const,
          ]
        : [],
    search: debouncedSearch,
    limit: 20,
    sort: [
      {
        direction: "asc",
        type: "name",
      },
    ],
  });

  const connectionList = supplierRelationshipListQuery.data?.pages.flat() ?? [];

  const toggleConnection = (connection: ItemType) => {
    setConnectionIds((prev) =>
      prev.includes(connection.id)
        ? prev.filter((connectionId) => connectionId !== connection.id)
        : [...prev, connection.id],
    );

    setConnectionsSelected((prev) => {
      const existing = prev.find((c) => c.id === connection.id);
      if (existing) {
        return prev.filter((c) => c.id !== connection.id);
      }
      return [...prev, connection];
    });
  };

  return (
    <div className="flex flex-col gap-4 pb-34">
      <div className="flex flex-col gap-2">
        <InputLabel>Search customers</InputLabel>
        <InputContainer>
          <Icon icon="MAGNIFY" />
          <InputText onChange={(e) => setSearch(e.target.value)} />
        </InputContainer>
      </div>
      {connectionsSelected.length > 0 && (
        <ExpandableSection
          expanded={selectedCustomersExpanded}
          onClick={() =>
            setSelectedCustomersExpanded(!selectedCustomersExpanded)
          }
          title={`${connectionsSelected.length} ${pluralize("customer", connectionsSelected.length)} selected`}
        >
          <div className="flex flex-col gap-2">
            {connectionsSelected.map((item, idx) => (
              <Item
                key={item.id}
                item={{
                  id: item.id,
                  name: item.name,
                  currentAssignment: item.currentAssignment,
                }}
                selected={true}
                alreadyAssigned={false}
                onToggle={toggleConnection}
                noBorder={idx + 1 === connectionsSelected.length}
              />
            ))}
          </div>
        </ExpandableSection>
      )}
      <InfiniteList
        query={supplierRelationshipListQuery}
        className="flex w-full flex-col rounded-lg border border-zinc-300"
      >
        {connectionList
          .filter((c) => !connectionsSelected.find((d) => d.id == c.id))
          .map((connection, idx) => {
            let currentAssignment =
              type === "product catalogue"
                ? connection.AvailabilityTier?.name
                : connection.PriceTier?.name;
            if (fulfillmentType) {
              if (fulfillmentType === "direct") {
                currentAssignment =
                  connection.DrayDeliveryMethod?.name ?? "inherited";
              }
              if (fulfillmentType === "courier") {
                currentAssignment =
                  connection.CourierDeliveryMethod?.name ?? "inherited";
              }
              if (fulfillmentType === "haulier") {
                currentAssignment =
                  connection.PalletDeliveryMethod?.name ?? "inherited";
              }
            }
            return (
              <Item
                key={connection.id}
                item={{
                  id: connection.id,
                  name: connection.Buyer.Business.name,
                  currentAssignment,
                }}
                selected={connectionIds.includes(connection.id)}
                alreadyAssigned={existingConnectionIds.includes(connection.id)}
                onToggle={toggleConnection}
                noBorder={
                  idx + 1 === connectionList.length - connectionsSelected.length
                }
              />
            );
          })}
      </InfiniteList>
    </div>
  );
};

const Item = ({
  item,
  selected,
  alreadyAssigned,
  onToggle,
  noBorder = false,
  className,
}: {
  item: ItemType;
  selected: boolean;
  alreadyAssigned: boolean;
  onToggle: (item: ItemType) => void;
  noBorder?: boolean;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-2 border-b border-zinc-300 p-2 text-sm",
        noBorder && "border-b-0",
        className,
      )}
    >
      <span className="flex items-center gap-2 font-semibold">
        {item.name}
        {" • "}
        <span className="font-normal text-zinc-500">
          {item.currentAssignment ?? "Not assigned"}
        </span>
      </span>
      {alreadyAssigned ? (
        <Button color="slim" disabled>
          Already assigned
        </Button>
      ) : (
        <Button
          color="slim"
          selected={selected}
          onClick={() => onToggle(item)}
          className="flex items-center gap-1"
        >
          {selected ? (
            <>
              Selected <Icon icon="CHECK" />
            </>
          ) : (
            "Select"
          )}
        </Button>
      )}
    </div>
  );
};

const ExpandableSection = ({
  children,
  title,
  expanded,
  onClick,
}: {
  children: ReactNode;
  title: string;
  expanded: boolean;
  onClick?: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={twMerge(
        "relative cursor-pointer overflow-hidden rounded-lg border border-zinc-300 bg-zinc-50 p-0 text-sm",
        expanded && "max-h-[1000px] overflow-y-auto",
      )}
    >
      <div className={"flex flex-col"}>
        <div className="grid w-full grid-cols-[1fr_min-content] gap-1 px-2 py-4">
          <span className="font-semibold">{title}</span>
          <figure className="flex items-center gap-2">
            {expanded ? "Hide" : "Show"}
            <Icon icon={expanded ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
          </figure>
        </div>
        <div
          className={twMerge("border-t border-zinc-300", !expanded && "hidden")}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
